import {
  migrateIfNeeded,
  validateScenario,
  type Scenario,
} from '@branchworks/shared';

/**
 * Load a scenario from arbitrary input (a parsed object, or a JSON string from
 * a file or an embedded global) and decide whether it's safe to play.
 *
 * This is the single entry pipeline shared by every "bring your own JSON" path:
 *   - the embedded global a standalone / SCORM bundle bakes in
 *     (`window.__BRANCHWORKS_SCENARIO__`)
 *   - the runtime's "Load file" picker (hand a Claude-authored scenario.json
 *     straight to the player, no editor in the loop)
 *
 * It mirrors the editor's import contract: migrate any older format up to the
 * current version, then run the shared validator. Validation *errors* block
 * play (a broken scenario shouldn't strand a learner mid-branch); *warnings*
 * are returned but don't block. Everything is in-memory — no network.
 */

export type LoadResult =
  | { ok: true; scenario: Scenario; warnings: string[] }
  | { ok: false; errors: string[] };

declare global {
  interface Window {
    /** Optional scenario baked into a standalone/SCORM bundle at build time. */
    __BRANCHWORKS_SCENARIO__?: unknown;
    /** Optional sink for session events (set by a SCORM/xAPI adapter). */
    __BRANCHWORKS_ON_EVENT__?: (event: unknown) => void;
    /** Optional exit handler (set by a SCORM adapter to call LMSFinish). */
    __BRANCHWORKS_ON_EXIT__?: () => void;
  }
}

function formatIssue(issue: { message: string; path?: { nodeLabel?: string; nodeId?: string } }): string {
  const label = issue.path?.nodeLabel || issue.path?.nodeId;
  return label ? `${label}: ${issue.message}` : issue.message;
}

export function loadScenario(raw: unknown): LoadResult {
  // 1. Parse if we were handed a string.
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return { ok: false, errors: [`File is not valid JSON: ${(e as Error).message}`] };
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, errors: ['Scenario must be a JSON object.'] };
  }

  // 2. Migrate to the current format version.
  let migrated: Scenario;
  try {
    migrated = migrateIfNeeded(parsed);
  } catch (e) {
    return { ok: false, errors: [`Could not read scenario: ${(e as Error).message}`] };
  }

  // 3. Minimal structural sanity before the full validator (it assumes these).
  if (!migrated.startNodeId || typeof migrated.nodes !== 'object' || migrated.nodes == null) {
    return { ok: false, errors: ['Scenario is missing a start node or its nodes map.'] };
  }
  if (!migrated.nodes[migrated.startNodeId]) {
    return { ok: false, errors: [`Start node "${migrated.startNodeId}" does not exist in nodes.`] };
  }

  // 4. Full validation. Errors block play; warnings are advisory.
  const result = validateScenario(migrated);
  if (result.errors.length > 0) {
    return { ok: false, errors: result.errors.map(formatIssue) };
  }
  return { ok: true, scenario: migrated, warnings: result.warnings.map(formatIssue) };
}
